import { useState, useEffect, useMemo } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { SectorAnalysisDiff, SectorAnalysis } from "@/types/risk-analysis";
import {
  formatProbabilityChange,
  formatImpactChange,
  formatScoreDelta,
  getScoreDeltaColor,
} from "@/utils/risk-diff";
import { formatScore } from "@/lib/utils";
import { ArrowRight } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import { CyFunLevelBadge } from "./CyFunLevelBadge";
import { MODAL_CONFIG } from "./RiskChangesModalConfig";

type ResetModalConfig = {
  mode: "reset";
  onConfirm: (selectedChangeIds: Set<string>) => void;
};

type SaveModalConfig = {
  mode: "save";
  onConfirm: (selectedChangeIds: Set<string>) => void;
  isSaving?: boolean;
};

type RiskChangesModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  diff: SectorAnalysisDiff | null;
  originalAnalysis: SectorAnalysis;
  currentAnalysis: SectorAnalysis;
} & (ResetModalConfig | SaveModalConfig);

export function RiskChangesModal(props: RiskChangesModalProps) {
  const {
    mode,
    open,
    onOpenChange,
    diff,
    originalAnalysis,
    currentAnalysis,
    onConfirm,
  } = props;
  const isSaving = props.mode === "save" ? props.isSaving : false;

  const config = MODAL_CONFIG[mode];
  const [selectedChanges, setSelectedChanges] = useState<Set<string>>(
    new Set()
  );

  // Initialize all changes as selected when modal opens
  useEffect(() => {
    if (open && diff) {
      const allChangeIds = new Set<string>();

      diff.categoryChanges.forEach((categoryChange) => {
        if (categoryChange.impactChange) {
          allChangeIds.add(`impact-${categoryChange.categoryId}`);
        }

        categoryChange.probabilityChanges.forEach((probChange) => {
          allChangeIds.add(
            `probability-${categoryChange.categoryId}-${probChange.actorId}`
          );
        });
      });

      setSelectedChanges(allChangeIds);
    }
  }, [open, diff]);

  // Toggle handler for checkboxes
  const handleToggleChange = (changeId: string) => {
    setSelectedChanges((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(changeId)) {
        newSet.delete(changeId);
      } else {
        newSet.add(changeId);
      }
      return newSet;
    });
  };

  // Calculate if all changes are selected
  const allChangesSelected = useMemo(() => {
    if (!diff) return true;

    let totalChanges = 0;
    diff.categoryChanges.forEach((cat) => {
      if (cat.impactChange) totalChanges++;
      totalChanges += cat.probabilityChanges.length;
    });

    return selectedChanges.size === totalChanges;
  }, [selectedChanges, diff]);

  // Dynamic button text
  const buttonText = diff?.hasChanges
    ? allChangesSelected
      ? config.buttonTextAll
      : config.buttonTextPartial
    : config.buttonTextNoChanges;

  if (!diff) return null;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-2xl max-h-[80vh] overflow-hidden">
        <AlertDialogHeader>
          <AlertDialogTitle>{config.title}</AlertDialogTitle>
          <AlertDialogDescription>
            {diff.hasChanges
              ? config.descriptionWithChanges
              : config.descriptionNoChanges}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {diff.hasChanges && (
          <ScrollArea className="max-h-[50vh]">
            <div className="space-y-4">
              {/* Summary Stats */}
              <div className="bg-muted/50 rounded-lg p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Scor Total:</span>
                  <div className="flex items-center gap-2">
                    <span className="text-sm">
                      {formatScore(diff.totalScoreOld)}
                    </span>
                    <ArrowRight className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-semibold">
                      {formatScore(diff.totalScoreNew)}
                    </span>
                    <span
                      className={`text-sm font-semibold ${getScoreDeltaColor(diff.totalScoreChange)}`}
                    >
                      ({formatScoreDelta(diff.totalScoreChange)})
                    </span>
                  </div>
                </div>

                {diff.cyfunLevelChange && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Nivel CyFun:</span>
                    <div className="flex items-center gap-2">
                      <CyFunLevelBadge level={diff.cyfunLevelChange.oldLevel} />
                      <ArrowRight className="h-4 w-4 text-muted-foreground" />
                      <CyFunLevelBadge level={diff.cyfunLevelChange.newLevel} />
                    </div>
                  </div>
                )}

                <div className="pt-2 border-t text-xs text-muted-foreground">
                  {diff.totalProbabilityChanges} modificări de probabilitate •{" "}
                  {diff.totalImpactChanges} modificări de impact
                </div>
              </div>

              {/* Category Changes */}
              {diff.categoryChanges.map((categoryChange) => (
                <div
                  key={categoryChange.categoryId}
                  className="border rounded-lg md:p-3 p-1.5 space-y-2"
                >
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium text-sm">
                      {categoryChange.categoryName}
                    </h4>
                    <span
                      className={`text-xs font-semibold ${getScoreDeltaColor(categoryChange.categoryScoreDelta)}`}
                    >
                      {formatScoreDelta(categoryChange.categoryScoreDelta)}
                    </span>
                  </div>

                  {/* Impact Change */}
                  {categoryChange.impactChange && (
                    <div className="text-sm bg-blue-50 dark:bg-blue-950/30 rounded p-2 flex items-center justify-between gap-2">
                      <Checkbox
                        id={`impact-${categoryChange.categoryId}`}
                        checked={selectedChanges.has(
                          `impact-${categoryChange.categoryId}`
                        )}
                        onCheckedChange={() =>
                          handleToggleChange(
                            `impact-${categoryChange.categoryId}`
                          )
                        }
                        className="flex-shrink-0"
                      />
                      <div className="w-full flex justify-between gap-2">
                        <span className="font-medium">Impact: </span>
                        <span>
                          {formatImpactChange(
                            categoryChange.impactChange.oldImpact,
                            categoryChange.impactChange.newImpact
                          )}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Probability Changes */}
                  {categoryChange.probabilityChanges.length > 0 && (
                    <div className="space-y-1">
                      {categoryChange.probabilityChanges.map((probChange) => (
                        <div
                          key={probChange.actorId}
                          className="text-sm flex items-center justify-between bg-muted/30 rounded px-2 py-1 gap-2"
                        >
                          <Checkbox
                            id={`probability-${categoryChange.categoryId}-${probChange.actorId}`}
                            checked={selectedChanges.has(
                              `probability-${categoryChange.categoryId}-${probChange.actorId}`
                            )}
                            onCheckedChange={() =>
                              handleToggleChange(
                                `probability-${categoryChange.categoryId}-${probChange.actorId}`
                              )
                            }
                            className="flex-shrink-0"
                          />
                          <span className="text-muted-foreground text-xs md:text-sm max-w-[100px] md:max-w-none">
                            {probChange.actorName}:
                          </span>
                          <div className="flex items-center md:gap-2 gap-1 flex-1 justify-end">
                            <span className="text-xs sm:text-sm">
                              {formatProbabilityChange(
                                probChange.oldProbability,
                                probChange.newProbability
                              )}
                            </span>
                            <span
                              className={`text-xs font-semibold ${getScoreDeltaColor(probChange.scoreDelta)}`}
                            >
                              ({formatScoreDelta(probChange.scoreDelta)})
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}

              {/* Warning banner for partial save */}
              {mode === "save" && !allChangesSelected && (
                <div className="bg-yellow-50 dark:bg-yellow-950/30 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
                  <p className="text-sm text-yellow-800 dark:text-yellow-200">
                    {MODAL_CONFIG.save.warningPartialSave}
                  </p>
                </div>
              )}
            </div>
          </ScrollArea>
        )}

        {!diff.hasChanges && (
          <div className="py-8 text-center text-muted-foreground">
            <p>{config.emptyStateMessage}</p>
            <p className="text-sm mt-2">{config.emptyStateSubMessage}</p>
          </div>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel>{config.cancelText}</AlertDialogCancel>
          <AlertDialogAction
            onClick={() => onConfirm(selectedChanges)}
            disabled={isSaving}
          >
            {isSaving ? "Se salvează..." : buttonText}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
